import { createContext } from '@chakra-ui/react-utils';
import { forwardRef } from '@chakra-ui/react-v2';
import type { ComponentWithAs, HTMLChakraProps } from '@chakra-ui/react-v2';
import { cx } from '@chakra-ui/utils';
import { AnimatePresence, motion } from 'framer-motion';

import { Badge, Box, type BadgeProps } from '@frontend/legacy-components';

type CountBadgeContextProps = {
  showBadge: boolean;
  badgeCount?: number;
  animateOnChange: boolean;
  showDot: boolean;
};

const [CountBadgeProvider, useCountBadgeContext] =
  createContext<CountBadgeContextProps>({
    name: 'CountBadgeContext',
    errorMessage:
      'useCountBadgeContext: context is undefined. Seems you forgot to wrap all components within <CountBadge>',
  });

type CountBadgeContainerProps = HTMLChakraProps<'div'> & {
  children: React.ReactNode;
  badgeCount?: number;
  showBadge?: boolean;
  animateOnChange?: boolean;
  showDot?: boolean;
  className?: string;
};

/**
 * CountBadge is a wrapper component that provides context for displaying badges on child elements.
 * It positions its children relatively and provides badge count and visibility state to child components.
 */
export const CountBadgeContainer: ComponentWithAs<
  'div',
  CountBadgeContainerProps
> = forwardRef<CountBadgeContainerProps, 'div'>(
  (
    {
      children,
      badgeCount,
      showBadge = false,
      animateOnChange = false,
      showDot = false,
      className,
      ...boxProps
    },
    ref,
  ) => {
    const _className = cx('count-badge', className);

    return (
      <CountBadgeProvider
        value={{ badgeCount, showBadge, animateOnChange, showDot }}
      >
        <Box
          position="relative"
          display="inline-block"
          {...boxProps}
          className={_className}
          ref={ref}
        >
          {children}
        </Box>
      </CountBadgeProvider>
    );
  },
);
CountBadgeContainer.displayName = 'CountBadgeContainer';

/**
 * CountBadgeBadge is a badge component that displays a count indicator.
 * It must be used within a CountBadge to access the badge context.
 * Badge states:
 * - badgeCount undefined + showDot false: small empty dot
 * - badgeCount undefined + showDot true: dot only
 * - badgeCount > 0: count display
 * - badgeCount > 0 + showDot true: dot + count
 * Counts over 99 are displayed as "99+".
 */
export const CountBadge: ComponentWithAs<'div', BadgeProps> = forwardRef<
  BadgeProps,
  'div'
>(({ className, ...badgeProps }, ref) => {
  const { badgeCount, showBadge, animateOnChange, showDot } =
    useCountBadgeContext();
  const _className = cx('count-badge__badge', className);

  const isTiny = showBadge && badgeCount === undefined && !showDot;

  return (
    <AnimatePresence initial={false}>
      {showBadge && (
        <Badge
          as={motion.div}
          key={animateOnChange ? `badge-${badgeCount}-${showDot}` : 'badge'}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: isTiny ? 0.4 : 1 }}
          exit={{ opacity: 0, scale: 0 }}
          ref={ref}
          position="absolute"
          borderRadius="full"
          top={-1}
          right={-1}
          className={_className}
          display="flex"
          alignItems="center"
          justifyContent="center"
          gap="0.25em"
          minW="1.5em"
          minH="1.5em"
          {...badgeProps}
        >
          <>
            {showDot && (
              <Box
                width="0.5em"
                height="0.5em"
                borderRadius="full"
                bg="currentColor"
              />
            )}
            {badgeCount !== undefined && (badgeCount > 99 ? '99+' : badgeCount)}
          </>
        </Badge>
      )}
    </AnimatePresence>
  );
});
CountBadge.displayName = 'CountBadge';
